import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTruck, FaWarehouse, FaGraduationCap, FaStore, FaArrowRight, FaStar } from 'react-icons/fa';
import { getProducts } from '../services/api';
import Loader from '../components/Loader';
import Reveal from '../components/Reveal';
import { gridContainer, gridItem, cardHover, heroContainer, viewportOnce } from '../components/motionPresets';
import { useT } from '../i18n/LanguageContext';
import './Home.css';

const T = {
    en: {
        heroTitle: 'Simplifying Supply Chains',
        heroSubtitle: 'Expert Solutions for Importation',
        heroText: 'Navigate the complexities of global trade with confidence. We provide expert solutions in importation, transit, and warehousing to streamline your supply chain.',
        visitStore: 'Visit Our Store',
        startLearning: 'Start Learning',
        servicesTitle: 'Our Services',
        servicesSubtitle: 'Everything you need to conquer global trade',
        onlineTraining: 'Online Training',
        onlineTrainingDesc: 'Comprehensive courses on importing, regulations, pricing strategies, and connecting with suppliers.',
        warehousing: 'Warehousing',
        warehousingDesc: 'Secure storage facilities in Yaounde and Douala with strict safety protocols and inventory management.',
        transit: 'Transit',
        transitDesc: 'Comprehensive transit services with expert customs clearance and customer-centric delivery solutions.',
        walkInStore: 'Walk-in Store',
        walkInStoreDesc: 'Visit our store for quality electronics: phones, accessories, chargers, audio gear, and much more.',
        learnMore: 'Learn More',
        shopNow: 'Shop Now',
        featuredTitle: 'Featured Products',
        featuredSubtitle: 'Quality electronics at the best prices',
        loadingProducts: 'Loading products',
        viewAllProducts: 'View All Products',
        whyTitle: 'Why Choose ElecSolution?',
        topQuality: 'Top Quality Products',
        topQualityDesc: 'We source only the best products from trusted international suppliers.',
        competitivePricing: 'Competitive Pricing',
        competitivePricingDesc: 'Direct importation means no middlemen and better prices for you.',
        expertGuidance: 'Expert Guidance',
        expertGuidanceDesc: 'Our training programs equip you with the skills to import like a pro.',
        reliableLogistics: 'Reliable Logistics',
        reliableLogisticsDesc: 'From warehousing to transit, we ensure your goods arrive safely and on time.',
        ctaTitle: 'Ready to Transform Your Importing Experience?',
        ctaText: 'Join our vibrant community and embark on a journey toward a more efficient and profitable future.',
        getInTouch: 'Get In Touch',
        joinCommunity: 'Join Our Community',
    },
    fr: {
        heroTitle: 'Simplifier les chaines d\'approvisionnement',
        heroSubtitle: 'Des solutions expertes pour l\'importation',
        heroText: 'Naviguez en toute confiance dans les complexites du commerce mondial. Nous proposons des solutions expertes en importation, transit et entreposage afin d\'optimiser votre chaine d\'approvisionnement.',
        visitStore: 'Visiter notre boutique',
        startLearning: 'Commencer a apprendre',
        servicesTitle: 'Nos services',
        servicesSubtitle: 'Tout ce qu\'il vous faut pour reussir dans le commerce mondial',
        onlineTraining: 'Formation en ligne',
        onlineTrainingDesc: 'Des cours complets sur l\'importation, la reglementation, les strategies de tarification et la mise en relation avec les fournisseurs.',
        warehousing: 'Entreposage',
        warehousingDesc: 'Des entrepots securises a Yaounde et Douala avec des protocoles de securite stricts et une gestion des stocks.',
        transit: 'Transit',
        transitDesc: 'Des services de transit complets avec un dedouanement expert et des solutions de livraison centrees sur le client.',
        walkInStore: 'Magasin physique',
        walkInStoreDesc: 'Visitez notre magasin pour des appareils electroniques de qualite : telephones, accessoires, chargeurs, materiel audio et bien plus encore.',
        learnMore: 'En savoir plus',
        shopNow: 'Acheter maintenant',
        featuredTitle: 'Produits en vedette',
        featuredSubtitle: 'Des appareils electroniques de qualite aux meilleurs prix',
        loadingProducts: 'Chargement des produits',
        viewAllProducts: 'Voir tous les produits',
        whyTitle: 'Pourquoi choisir ElecSolution ?',
        topQuality: 'Produits de premiere qualite',
        topQualityDesc: 'Nous selectionnons uniquement les meilleurs produits aupres de fournisseurs internationaux de confiance.',
        competitivePricing: 'Prix competitifs',
        competitivePricingDesc: 'L\'importation directe signifie aucun intermediaire et de meilleurs prix pour vous.',
        expertGuidance: 'Accompagnement expert',
        expertGuidanceDesc: 'Nos programmes de formation vous donnent les competences pour importer comme un professionnel.',
        reliableLogistics: 'Logistique fiable',
        reliableLogisticsDesc: 'De l\'entreposage au transit, nous garantissons que vos marchandises arrivent en toute securite et a temps.',
        ctaTitle: 'Pret a transformer votre experience d\'importation ?',
        ctaText: 'Rejoignez notre communaute dynamique et entamez un parcours vers un avenir plus efficace et plus rentable.',
        getInTouch: 'Nous contacter',
        joinCommunity: 'Rejoindre notre communaute',
    },
};

function Home() {
    const t = useT(T);
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [loadingFeatured, setLoadingFeatured] = useState(true);

    useEffect(() => {
        getProducts()
            .then(res => setFeaturedProducts(res.data.slice(0, 8)))
            .catch(() => {})
            .finally(() => setLoadingFeatured(false));
    }, []);

    return (
        <div className="home">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-slideshow">
                    <div className="hero-slide"></div>
                    <div className="hero-slide"></div>
                    <div className="hero-slide"></div>
                    <div className="hero-slide"></div>
                    <div className="hero-slide"></div>
                    <div className="hero-slide"></div>
                </div>
                <div className="hero-slideshow-overlay"></div>
                <div className="hero-overlay">
                    <motion.div
                        className="hero-content"
                        initial="hidden"
                        animate="show"
                        variants={heroContainer}
                    >
                        <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                        <motion.h2 variants={gridItem}>{t.heroSubtitle}</motion.h2>
                        <motion.p variants={gridItem}>
                            {t.heroText}
                        </motion.p>
                        <motion.div className="hero-buttons" variants={gridItem}>
                            <Link to="/products" className="btn btn-primary">
                                {t.visitStore} <FaArrowRight />
                            </Link>
                            <Link to="/training" className="btn btn-secondary">
                                {t.startLearning}
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Services Section */}
            <section className="services-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.servicesTitle}</h2></Reveal>
                    <Reveal delay={0.08}><p className="section-subtitle">{t.servicesSubtitle}</p></Reveal>
                    <motion.div
                        className="services-grid"
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                    >
                        <motion.div className="service-card" variants={gridItem} whileHover={cardHover}>
                            <div className="service-icon"><FaGraduationCap /></div>
                            <h3>{t.onlineTraining}</h3>
                            <p>{t.onlineTrainingDesc}</p>
                            <Link to="/training" className="service-link">{t.learnMore} <FaArrowRight /></Link>
                        </motion.div>
                        <motion.div className="service-card" variants={gridItem} whileHover={cardHover}>
                            <div className="service-icon"><FaWarehouse /></div>
                            <h3>{t.warehousing}</h3>
                            <p>{t.warehousingDesc}</p>
                            <Link to="/warehousing" className="service-link">{t.learnMore} <FaArrowRight /></Link>
                        </motion.div>
                        <motion.div className="service-card" variants={gridItem} whileHover={cardHover}>
                            <div className="service-icon"><FaTruck /></div>
                            <h3>{t.transit}</h3>
                            <p>{t.transitDesc}</p>
                            <Link to="/transit" className="service-link">{t.learnMore} <FaArrowRight /></Link>
                        </motion.div>
                        <motion.div className="service-card" variants={gridItem} whileHover={cardHover}>
                            <div className="service-icon"><FaStore /></div>
                            <h3>{t.walkInStore}</h3>
                            <p>{t.walkInStoreDesc}</p>
                            <Link to="/products" className="service-link">{t.shopNow} <FaArrowRight /></Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="featured-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.featuredTitle}</h2></Reveal>
                    <Reveal delay={0.08}><p className="section-subtitle">{t.featuredSubtitle}</p></Reveal>
                    {loadingFeatured && <Loader label={t.loadingProducts} />}
                    {!loadingFeatured && (
                    <motion.div
                        className="products-grid"
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        {featuredProducts.map(product => (
                            <motion.div key={product.id} className="product-card" variants={gridItem} whileHover={cardHover}>
                                <div className="product-img-placeholder">
                                    {product.image_url
                                        ? <img src={product.image_url} alt={product.name} className="product-thumb-img" />
                                        : <FaStore />}
                                </div>
                                <div className="product-info">
                                    <h4>{product.name}</h4>
                                    <p className="product-category">{product.category}</p>
                                    <p className="product-price">{Number(product.price).toLocaleString()} XAF</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                    )}
                    <div className="center-btn">
                        <Link to="/products" className="btn btn-primary">
                            {t.viewAllProducts} <FaArrowRight />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="why-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.whyTitle}</h2></Reveal>
                    <motion.div
                        className="why-grid"
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                    >
                        <motion.div className="why-card" variants={gridItem} whileHover={cardHover}>
                            <FaStar className="why-icon" />
                            <h4>{t.topQuality}</h4>
                            <p>{t.topQualityDesc}</p>
                        </motion.div>
                        <motion.div className="why-card" variants={gridItem} whileHover={cardHover}>
                            <FaStar className="why-icon" />
                            <h4>{t.competitivePricing}</h4>
                            <p>{t.competitivePricingDesc}</p>
                        </motion.div>
                        <motion.div className="why-card" variants={gridItem} whileHover={cardHover}>
                            <FaStar className="why-icon" />
                            <h4>{t.expertGuidance}</h4>
                            <p>{t.expertGuidanceDesc}</p>
                        </motion.div>
                        <motion.div className="why-card" variants={gridItem} whileHover={cardHover}>
                            <FaStar className="why-icon" />
                            <h4>{t.reliableLogistics}</h4>
                            <p>{t.reliableLogisticsDesc}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <Reveal className="cta-content" y={36}>
                    <h2>{t.ctaTitle}</h2>
                    <p>{t.ctaText}</p>
                    <div className="hero-buttons">
                        <Link to="/contact" className="btn btn-primary">{t.getInTouch}</Link>
                        <Link to="/training" className="btn btn-secondary">{t.joinCommunity}</Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}

export default Home;
